import { useEffect, useMemo, useRef } from 'react';
import { isIOS, isMobile, isTablet } from 'react-device-detect';
import { AppRoutes, BannerType } from 'src/types';
import { CSSTransition } from 'react-transition-group';
// import { InsufficientCreditsBanner } from 'src/pages/ManageTasksChatPage/components/InsufficientCreditsBanner';
import { useBanner } from 'src/hooks/useBanner';
// import { LowCreditsBanner } from 'src/pages/ManageTasksChatPage/components/LowCreditsBanner';
import { ANIMATION_TIMEOUT, SVG_SIZE_M } from 'src/constants';
import { BannerPagination } from 'src/components/Banner/components/BannerPagination';
import { X } from '@phosphor-icons/react';
import { LowTasksBanner } from 'src/pages/ManageTasksChatPage/components/LowTasksBanner';
import { useGetUser, useSession } from 'src/hooks';
import { useLazyGetUserTaskQuotaInfoQuery } from 'src/store/services';
import { LowDaysBanner } from 'src/pages/ManageTasksChatPage/components/LowDaysBanner';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import { UserStatus } from 'src/types/models/UserStatus';
import { UserRateLimitedBanner } from 'src/pages/ManageTasksChatPage/components/UserRateLimitedBanner';
import { DownloadMobileAppBanner } from 'src/v2/components/DownloadMobileAppBanner';
import classNames from 'classnames';
import { useFeatureFlagEnabled } from 'posthog-js/react';

export const Banner = () => {
  const { pathname } = useLocation();
  const {
    bannersSet,
    removeBanner,
    currentBannerIndex,
    addBanner,
    removeAllPaymentBanners,
    addToBlackListBanner,
    blackListBanners,
  } = useBanner();
  const {
    appUser,
    isProTrialTier,
    isFreeTier,
    isOpenTier,
    isEnterpriseTrialTier,
  } = useSession();
  const { isTeamsTier } = useGetUser();
  // const [triggerGetUserBudget] = useLazyGetUserBudgetQuotaInfoQuery();
  const [triggerGetUserTasksCount] = useLazyGetUserTaskQuotaInfoQuery();
  const showBusinessMigrationBanner = useFeatureFlagEnabled(
    'showBusinessMigrationBanner',
  );

  // TODO(olha): temporary workaround
  const hideBanner = useMemo(() => {
    return [
      AppRoutes.LOGIN,
      AppRoutes.LOGIN_WITH_EMAIL,
      AppRoutes.SIGN_UP,
      // Email signup route commented out - enforcing SSO-only signup
      // AppRoutes.SIGN_UP_WITH_EMAIL,
      AppRoutes.LOGIN_ERROR,
      AppRoutes.FORGOT_PASSWORD,
      AppRoutes.RESET_PASSWORD,
      AppRoutes.VERIFICATION_FAILED,
      AppRoutes.VERIFIED,
      AppRoutes.PASSWORD_RESET_FAILED,
      AppRoutes.PAYMENT,
    ].includes(pathname as AppRoutes);
  }, [pathname]);

  // const checkBudget = async () => {
  //   const { data: budgetData } = await triggerGetUserBudget({
  //     user_id: appUser?.user_id || '',
  //   });

  //   if (budgetData && budgetData.is_low_balance) {
  //     addBanner(BannerType.LOW_CREDITS);
  //   }
  // };

  const checkTasks = async () => {
    const { data: quotaData } = await triggerGetUserTasksCount({
      user_id: appUser?.user_id || '',
    });

    if (quotaData && quotaData?.count !== null && quotaData?.count <= 3) {
      addBanner(BannerType.LOW_TASKS);
    }
  };

  useEffect(() => {
    removeAllPaymentBanners();

    // (irina): it was an urgent task to show the banner for ios app
    if (
      !!blackListBanners[BannerType.DOWNLOAD_MOBILE_APP] &&
      !blackListBanners[BannerType.DOWNLOAD_MOBILE_APP]?.isHidden &&
      (isMobile || isTablet) &&
      isIOS
    ) {
      addBanner(BannerType.DOWNLOAD_MOBILE_APP);
    }

    if (
      isProTrialTier &&
      !!blackListBanners[BannerType.LOW_DAYS] &&
      !blackListBanners[BannerType.LOW_DAYS]?.isHidden
    ) {
      addBanner(BannerType.LOW_DAYS);
    }

    if (
      isEnterpriseTrialTier &&
      !!blackListBanners[BannerType.LOW_DAYS] &&
      !blackListBanners[BannerType.LOW_DAYS]?.isHidden
    ) {
      const diff = Math.ceil(
        dayjs(appUser?.tier_expiration_date).diff(dayjs(), 'day', true),
      );
      if (diff <= 7) {
        addBanner(BannerType.LOW_DAYS);
      }
    }

    // if (isProTier || isProTrialTier || isEnterpriseTier) {
    //   if (appUser.tier_status === UserTierStatus.QUOTA_EXCEEDED) {
    //     // addBanner(BannerType.INSUFFICIENT_CREDITS);
    //   } else {
    //     checkBudget();
    //   }
    // }

    if (isFreeTier || isOpenTier) {
      checkTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appUser.tier_id, appUser.tier_expiration_date, blackListBanners]);

  useEffect(() => {
    if (appUser.status === UserStatus.RATE_LIMITED) {
      addBanner(BannerType.USER_RATE_LIMITED);
    } else {
      removeBanner(BannerType.USER_RATE_LIMITED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appUser.status]);

  useEffect(() => {
    if (isTeamsTier && showBusinessMigrationBanner) {
      addBanner(BannerType.BUSINESS_MIGRATION);
    } else {
      removeBanner(BannerType.BUSINESS_MIGRATION);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeamsTier, showBusinessMigrationBanner]);

  useEffect(() => {
    if (!appUser.rate_limited_until) {
      removeBanner(BannerType.USER_RATE_LIMITED_UNTIL);
      return;
    }

    const currentTime = dayjs();
    const rateLimitedTime = dayjs(appUser.rate_limited_until);

    if (rateLimitedTime.isAfter(currentTime)) {
      addBanner(BannerType.USER_RATE_LIMITED_UNTIL);
    } else {
      removeBanner(BannerType.USER_RATE_LIMITED_UNTIL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appUser.rate_limited_until]);

  const containerRef = useRef<HTMLDivElement>(null);

  const currentBanner = useMemo(
    () => bannersSet?.[currentBannerIndex],
    [bannersSet, currentBannerIndex],
  );

  const renderContent = useMemo(() => {
    switch (currentBanner) {
      // case BannerType.LOW_CREDITS:
      //   return <LowCreditsBanner />;
      // case BannerType.INSUFFICIENT_CREDITS:
      //   return <InsufficientCreditsBanner />;
      case BannerType.LOW_TASKS:
        return <LowTasksBanner />;
      case BannerType.LOW_DAYS:
        return <LowDaysBanner />;
      case BannerType.USER_RATE_LIMITED:
        return <UserRateLimitedBanner />;
      case BannerType.USER_RATE_LIMITED_UNTIL:
        return <UserRateLimitedBanner isLimitedUntil />;
      case BannerType.DOWNLOAD_MOBILE_APP:
        return <DownloadMobileAppBanner />;
      default:
        return <></>;
    }
  }, [currentBanner]);

  useEffect(() => {
    if (currentBanner === BannerType.DOWNLOAD_MOBILE_APP && !isIOS) {
      removeBanner(BannerType.DOWNLOAD_MOBILE_APP);
    }
  }, [currentBanner, removeBanner]);

  const handleCloseBanner = () => {
    if (
      currentBanner === BannerType.LOW_DAYS ||
      currentBanner === BannerType.DOWNLOAD_MOBILE_APP
    ) {
      addToBlackListBanner(currentBanner);
    }

    removeBanner(currentBanner);
  };

  return (
    <CSSTransition
      in={bannersSet.length !== 0 && !hideBanner}
      timeout={ANIMATION_TIMEOUT}
      classNames="nj-animate-vertical-appearance-banner"
      unmountOnExit
      nodeRef={containerRef}
    >
      <div
        ref={containerRef}
        className={classNames('nj-banner-wrapper', {
          'nj-banner-wrapper--without-pagination': bannersSet.length === 1,
          'nj-banner-wrapper--with-pagination': bannersSet.length > 1,
        })}
      >
        {bannersSet.length > 1 && <BannerPagination />}
        {renderContent}
        <button
          type="button"
          onClick={handleCloseBanner}
          className="nj-banner-close-button"
        >
          <X size={SVG_SIZE_M} />
        </button>
      </div>
    </CSSTransition>
  );
};
